import React, { useMemo } from "react";

/**
 * Traditional North Indian Diamond Vedic Kundli Chart
 * Diamond layout with triangular and square houses
 */

const SIGNS = ["Aries","Taurus","Gemini","Cancer","Leo","Virgo","Libra","Scorpio","Sagittarius","Capricorn","Aquarius","Pisces"];

export default function SouthIndianChart({ ascendant, chartData }) {
  const ascIndex = SIGNS.indexOf(ascendant);
  
  // Debug log (development only)
  if (process.env.NODE_ENV === 'development') {
  console.log('SouthIndianChart rendered with:', { ascendant, chartData, version: '2.0-colored-planets' });
  }

  // Calculate which sign is in each house
  const houses = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const signIndex = (ascIndex + i) % 12;
      const sign = SIGNS[signIndex];
      const signNumber = signIndex + 1;
      
      // Convert chartData object to array if needed
      let planetsArray = [];
      if (Array.isArray(chartData)) {
        planetsArray = chartData;
      } else if (chartData && typeof chartData === 'object') {
        // chartData is an object like {sun: {...}, moon: {...}}
        planetsArray = Object.keys(chartData).map(key => {
          const planetData = chartData[key];
          return {
            planet: key.charAt(0).toUpperCase() + key.slice(1), // Capitalize planet name
            sign: planetData?.sign,
            house: planetData?.house
          };
        });
      }
      
      // Find planets in this sign
      const planets = planetsArray
        .filter(p => p && p.sign === sign)
        .map(p => p.planet || 'Unknown');
      
      return {
        houseNumber: i + 1,
        sign,
        signNumber,
        planets
      };
    });
  }, [chartData, ascIndex]);

  // Planet colors matching D9 circular chart colors
  const PLANET_COLORS = {
    Sun: '#FFA500',      // Orange
    Moon: '#C0C0C0',     // Silver
    Mars: '#FF6B47',     // Red
    Mercury: '#8C7853',  // Brown
    Jupiter: '#C88B3A',  // Golden
    Venus: '#FFC649',    // Light Gold
    Saturn: '#FAD5A5',   // Beige
    Rahu: '#8B7355',     // Dark Brown
    Ketu: '#6B4423'      // Darker Brown
  };
  
  // Planet short names
  const PLANET_SHORT = {
    Sun: 'Su',
    Moon: 'Mo',
    Mars: 'Ma',
    Mercury: 'Me',
    Jupiter: 'Ju',
    Venus: 'Ve',
    Saturn: 'Sa',
    Rahu: 'Ra',
    Ketu: 'Ke'
  };

  // Increased canvas size - House positions scaled to 700x700 for more space
  // Adjusted to prevent overlap between house labels and planets
  const houseTextPositions = [
    { x: 350, y: 175, labelX: 350, labelY: 60 },    // House 1 - center top square
    { x: 175, y: 84, labelX: 175, labelY: 40 },     // House 2 - top left triangle
    { x: 84, y: 175, labelX: 25, labelY: 165 },     // House 3 - left top triangle
    { x: 175, y: 350, labelX: 65, labelY: 330 },    // House 4 - left center square
    { x: 84, y: 525, labelX: 25, labelY: 540 },     // House 5 - left bottom triangle
    { x: 175, y: 616, labelX: 175, labelY: 680 },   // House 6 - bottom left triangle
    { x: 350, y: 525, labelX: 350, labelY: 650 },   // House 7 - center bottom square
    { x: 525, y: 616, labelX: 525, labelY: 680 },   // House 8 - bottom right triangle
    { x: 616, y: 525, labelX: 675, labelY: 540 },   // House 9 - right bottom triangle
    { x: 525, y: 350, labelX: 635, labelY: 330 },   // House 10 - right center square
    { x: 616, y: 175, labelX: 675, labelY: 165 },   // House 11 - right top triangle
    { x: 525, y: 84, labelX: 525, labelY: 40 }      // House 12 - top right triangle
  ];

  return (
    <div className="w-full flex justify-center p-4">
      <svg 
        viewBox="0 0 700 700" 
        width="100%" 
        style={{ maxWidth: '550px', display: 'block' }}
      >
        {/* Sacred gradient background */}
        <defs>
          <linearGradient id="sacredBg" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style={{ stopColor: '#FFF8DC', stopOpacity: 1 }} />
            <stop offset="50%" style={{ stopColor: '#FFFEF0', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: '#FFF8DC', stopOpacity: 1 }} />
          </linearGradient>
          <radialGradient id="centerGlow">
            <stop offset="0%" style={{ stopColor: '#FFD700', stopOpacity: 0.3 }} />
            <stop offset="100%" style={{ stopColor: '#FFD700', stopOpacity: 0 }} />
          </radialGradient>
        </defs>
        
        {/* Background with sacred glow */}
        <rect x="0" y="0" width="700" height="700" fill="url(#sacredBg)" />
        
        {/* Central sacred glow */}
        <circle cx="350" cy="350" r="250" fill="url(#centerGlow)" />
        
        {/* Outer diamond border - Triple sacred border */}
        <polygon 
          points="350,0 700,350 350,700 0,350"
          fill="none" 
          stroke="#FF6B00" 
          strokeWidth="4"
        />
        <polygon 
          points="350,5 695,350 350,695 5,350"
          fill="none" 
          stroke="#FFD700" 
          strokeWidth="2"
        />
        <polygon 
          points="350,10 690,350 350,690 10,350"
          fill="none" 
          stroke="#FF6B00" 
          strokeWidth="1"
        />
        
        {/* House 1 - Center top square (Lagna - most sacred) */}
        <polygon points="350,0 525,175 350,350 175,175" fill="none" stroke="#FF8C00" strokeWidth="2.5" opacity="0.8" />
        
        {/* House 2 - Top left triangle */}
        <polygon points="0,0 350,0 175,175" fill="none" stroke="#CD853F" strokeWidth="2" opacity="0.7" />
        
        {/* House 3 - Left top triangle */}
        <polygon points="0,0 175,175 0,350" fill="none" stroke="#CD853F" strokeWidth="2" opacity="0.7" />
        
        {/* House 4 - Left center square */}
        <polygon points="175,175 350,350 175,525 0,350" fill="none" stroke="#DAA520" strokeWidth="2" opacity="0.7" />
        
        {/* House 5 - Left bottom triangle */}
        <polygon points="0,350 175,525 0,700" fill="none" stroke="#CD853F" strokeWidth="2" opacity="0.7" />
        
        {/* House 6 - Bottom left triangle */}
        <polygon points="175,525 350,700 0,700" fill="none" stroke="#CD853F" strokeWidth="2" opacity="0.7" />
        
        {/* House 7 - Center bottom square */}
        <polygon points="350,350 525,525 350,700 175,525" fill="none" stroke="#DAA520" strokeWidth="2" opacity="0.7" />
        
        {/* House 8 - Bottom right triangle */}
        <polygon points="525,525 700,700 350,700" fill="none" stroke="#CD853F" strokeWidth="2" opacity="0.7" />
        
        {/* House 9 - Right bottom triangle */}
        <polygon points="525,525 700,350 700,700" fill="none" stroke="#CD853F" strokeWidth="2" opacity="0.7" />
        
        {/* House 10 - Right center square */}
        <polygon points="525,175 700,350 525,525 350,350" fill="none" stroke="#DAA520" strokeWidth="2" opacity="0.7" />
        
        {/* House 11 - Right top triangle */}
        <polygon points="700,0 700,350 525,175" fill="none" stroke="#CD853F" strokeWidth="2" opacity="0.7" />
        
        {/* House 12 - Top right triangle */}
        <polygon points="350,0 700,0 525,175" fill="none" stroke="#CD853F" strokeWidth="2" opacity="0.7" />
        
        {/* Sacred Om symbol in center */}
        <text
          x="350"
          y="360"
          textAnchor="middle"
          fontSize="32"
          fill="#FF6B00"
          fontWeight="bold"
          fontFamily="Georgia, serif"
          opacity="0.2"
        >
          ॐ
        </text>
        
        {/* House labels and content */}
        {houses.map((house, idx) => {
          const pos = houseTextPositions[idx];
          
          return (
            <g key={idx}>
              {/* House number label - positioned to avoid overlap */}
              <text
                x={pos.labelX}
                y={pos.labelY}
                textAnchor="middle"
                fontSize="11"
                fill="#8B4513"
                fontWeight="600"
                fontFamily="Georgia, serif"
              >
                {house.houseNumber}{house.houseNumber === 1 ? 'st' : house.houseNumber === 2 ? 'nd' : house.houseNumber === 3 ? 'rd' : 'th'} House
              </text>
              
              {/* Rashi number - large and centered with sacred color */}
              <text
                x={pos.x}
                y={pos.y + 5}
                textAnchor="middle"
                fontSize="28"
                fill={house.houseNumber === 1 ? '#FF6B00' : '#1A1A1A'}
                fontWeight="700"
                fontFamily="Georgia, serif"
              >
                {house.signNumber}
              </text>
              
              {/* Sign name - below rashi number */}
              <text
                x={pos.x}
                y={pos.y + 25}
                textAnchor="middle"
                fontSize="12"
                fill="#8B4513"
                fontWeight="500"
                fontFamily="Georgia, serif"
              >
                {house.sign}
              </text>
              
              {/* Planets - small colored circles with short names */}
              {house.planets.map((planet, pIdx) => {
                const planetColor = PLANET_COLORS[planet] || '#666666';
                const planetShort = PLANET_SHORT[planet] || planet.substring(0, 2);
                const yOffset = 42 + (pIdx * 18);
                
                return (
                  <g key={pIdx}>
                    {/* Small planet circle */}
                    <circle
                      cx={pos.x}
                      cy={pos.y + yOffset}
                      r="6"
                      fill={planetColor}
                      stroke="#333"
                      strokeWidth="0.5"
                    />
                    {/* Short planet name */}
                    <text
                      x={pos.x + 10}
                      y={pos.y + yOffset + 4}
                      fontSize="9"
                      fill="#2C1810"
                      fontWeight="500"
                      fontFamily="Georgia, serif"
                    >
                      {planetShort}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

